import React from 'react';
import {ForecastMapService} from '../api';
import {
  ForecastMapBuildConflict,
  ForecastMapRunStatusObject,
} from '../api/types';
import useApiClient from './useApiClient';

// Run polling cadence. Fixed interval (progress is monotonic); generous
// attempt budget — a full run renders dozens of frames.
export const BUILD_POLL_MS = 2500;
export const BUILD_POLL_MAX_ATTEMPTS = 480; // ~20 minutes

export type BuildState =
  | {status: 'idle'}
  | {status: 'starting'}
  | {status: 'progress'; run: ForecastMapRunStatusObject}
  | {status: 'completed'; run: ForecastMapRunStatusObject}
  | {status: 'failed'}
  // 409 without a run: nothing is buildable yet (no completed forecast
  // run / metrics not processed) — nothing was created server-side.
  | {status: 'unavailable'; detail: string | null};

/**
 * Build flow: POST configs/{id}/build/ answers 202 (new run) or 200
 * (already built for the latest forecast run) with a run-status body, or
 * 409 — either {detail, run} while a build is in progress (poll that
 * run's progress) or {detail} when nothing is buildable. Progress is
 * polled via GET runs/{id}/ until completed/failed; polling stops on
 * unmount and when a new build supersedes the chain.
 */
const useForecastMapBuild = () => {
  const {fetch} = useApiClient();
  const [state, setState] = React.useState<BuildState>({status: 'idle'});
  const seqRef = React.useRef(0);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  React.useEffect(
    () => () => {
      seqRef.current += 1;
      clearTimer();
    },
    [clearTimer],
  );

  const start = React.useCallback(
    async (configId: number) => {
      const seq = ++seqRef.current;
      clearTimer();
      setState({status: 'starting'});

      function handleRun(run: ForecastMapRunStatusObject, attempt: number) {
        if (run.status === 'completed') {
          setState({status: 'completed', run});
          return;
        }
        if (run.status === 'failed') {
          setState({status: 'failed'});
          return;
        }
        setState({status: 'progress', run});
        if (attempt + 1 >= BUILD_POLL_MAX_ATTEMPTS) {
          setState({status: 'failed'});
          return;
        }
        timerRef.current = setTimeout(async () => {
          try {
            // silent: a network blip during a long-running build poll must
            // not raise the global alert every cycle — failures retry and
            // terminal states are surfaced inline by the save/build bar.
            const polled = await fetch(
              ForecastMapService.forecastMapRunDetail(run.id),
              {silent: true},
            );
            if (seqRef.current !== seq) {
              return;
            }
            handleRun(polled, attempt + 1);
          } catch {
            if (seqRef.current !== seq) {
              return;
            }
            // Transient poll failure — keep the last progress and retry.
            handleRun(run, attempt + 1);
          }
        }, BUILD_POLL_MS);
      }

      try {
        // silent: every outcome (progress, conflict, unavailable, failed)
        // is rendered inline by the save/build bar.
        const run = await fetch(
          ForecastMapService.forecastMapConfigBuild(configId),
          {ignore409: true, silent: true},
        );
        if (seqRef.current !== seq) {
          return;
        }
        handleRun(run, 0);
      } catch (error) {
        if (seqRef.current !== seq) {
          return;
        }
        const statusCode = (error as {statusCode?: number})?.statusCode;
        if (statusCode === 409) {
          const body = (error as {data?: ForecastMapBuildConflict})?.data;
          if (body?.run) {
            // A build is already in progress — surface its progress.
            handleRun(body.run, 0);
            return;
          }
          setState({status: 'unavailable', detail: body?.detail ?? null});
          return;
        }
        setState({status: 'failed'});
      }
    },
    [fetch, clearTimer],
  );

  const reset = React.useCallback(() => {
    seqRef.current += 1;
    clearTimer();
    setState({status: 'idle'});
  }, [clearTimer]);

  return {state, start, reset};
};

export default useForecastMapBuild;
